import { cacheAllModuleFiles } from '@/store/sync/cachingUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import { SYNC_STATES } from '@/store/sync/sync';
import dataUtils from '@/common/dataUtils';

import moduleIndex from './index.js';
import welcomeApi from './welcomeApi';
import welcomeService from './welcomeService';

const MODULE_ID = 'welcome';
const ROOT = { root: true };

const state = {
  welcomeItems: [],
  welcomeMediaItems: [],
  welcomeItemsById: {},

  // view state
  welcomeItemToDisplay: null,
  showWelcome: true,
  isLoaded: false,
};
const getters = {
  /** -------------------------------------------- ----------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    // module does not support instance
    return 0;
  },
  showWelcomeScreen(state, getters, rootState, rootGetters) {
    // console.debug("rootGetters['lifecycle/isAuthenticated'] ",rootGetters['lifecycle/isAuthenticated'] )
    // console.debug("rootGetters['lifecycle/isReady'] ",rootGetters['lifecycle/isReady'] )
    // console.debug("showWelcome'] ",state.showWelcome)
    // console.debug("welcomeItemToDisplay'] ",state.welcomeItemToDisplay)
    return (rootGetters['lifecycle/isAuthenticated'] || rootGetters['lifecycle/isReady']) && state.showWelcome && state.welcomeItemToDisplay;
  },
};
const mutations = {
  setShowWelcome(state, value) {
    console.debug('mutation setShowWelcome', value);
    state.showWelcome = value;
  },
  setData(state, { welcomeItemToDisplay, welcomeItems, welcomeItemsById, welcomeMediaItems }) {
    state.welcomeItemToDisplay = welcomeItemToDisplay;
    state.welcomeItems = welcomeItems;
    state.welcomeMediaItems = Object.freeze(welcomeMediaItems);
    state.welcomeItemsById = Object.freeze(welcomeItemsById);
    state.isLoaded = true;
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {},
  markWelcomeItemAsRead(state, { welcomeItem, dontShowAgain }) {
    // console.debug('markWelcomeItemAsRead', welcomeItem.id);

    const dateAsMsFromEpoch = new Date();
    welcomeItem.read = dateAsMsFromEpoch;
    welcomeItem.doNotShowAgain = dontShowAgain;

    welcomeService.updateLocalStorage(state.welcomeItems);

    const welcomeItemsToDisplay = welcomeService.getCurrentWelcomeItems(dateAsMsFromEpoch, state.welcomeItems);
    state.welcomeItemToDisplay = welcomeItemsToDisplay.length > 0 ? welcomeItemsToDisplay[0] : null;
  },
  setIsWelcomeDialogVisible(state) {
    if (state.welcomeItems.length > 0) {
      state.welcomeItemToDisplay = state.welcomeItems[0];
    }
  },
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId: MODULE_ID,
      items: state.welcomeMediaItems,
      titleSearchAttr: 'text',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    //const { welcomeItems, welcomeMediaItems } = state;
    //await updateModuleCache(moduleIndex.api, { welcomeItems, welcomeMediaItems });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    console.debug('welcome synchronizeFiles');
    await cacheAllModuleFiles({ files: state.welcomeMediaItems, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, dispatch, rootState, rootGetters }, { syncAfterAuthentication }) {
    console.debug('synchronizeData in welcome Store');
    try {
      const response = await welcomeApi.getData();
      let { welcomeItems, welcomeMediaItems } = response.data;

      if (welcomeItems.length === 0 && rootState.configs.features.showEmptyWelcomeScreen) {
        // add an artificial item for the case we show an empty dialog
        welcomeItems.push({
          id: -1,
          text: 'Pseudo Element',
          anzeige_von: '2021-09-01T22:00:00.000Z',
          anzeige_bis: null,
        });
      }

      let welcomeItemsById = dataUtils.buildIdMap(welcomeItems, 'id');

      const welcomeMediaItemsById = dataUtils.buildIdMap(welcomeMediaItems, 'id');
      welcomeMediaItems.forEach((m) => {
        if (welcomeItemsById[m.welcomeIdFk]) {
          welcomeItemsById[m.welcomeIdFk]._file = m;
        }
      });

      // enhance with stored localStorage data and update localStorage at the same time (removing inexisting items)
      welcomeService.synchronizeWithLocalStorage(welcomeItems);
      welcomeService.updateLocalStorage(welcomeItems);
      if (syncAfterAuthentication) {
        // check if items have to be shown again after reload or login
        console.debug('// check if items have to be shown again after reload or login');
      }
      const dateAsMsFromEpoch = new Date(Date.now());
      const welcomeItemsToDisplay = welcomeService.getCurrentWelcomeItems(dateAsMsFromEpoch, welcomeItems, syncAfterAuthentication);

      commit('setData', {
        welcomeItemToDisplay: welcomeItemsToDisplay.length > 0 ? welcomeItemsToDisplay[0] : null,
        welcomeItems,
        welcomeMediaItems,
        welcomeItemsById,
      });

      // await dispatch('updateModuleApplicationCache');
      console.debug('welcome data synchronized');
    } catch (err) {
      console.error(err);
      commit('sync/setSyncState', { moduleId: MODULE_ID, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
